import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useBlobStorageFileVersions } from '../../hooks/useBlobStorageFileVersions';
import { DateTime } from 'luxon';
import { BlobDocumentType } from '../../gql-types.generated';
import { Grid, Typography } from '@mui/material';
import FileIcon from '@mui/icons-material/Description';
import { CardAvatar, DetailsTabContainer, DetailsTabHeader, DetailsTabHeaderWrapper, DetailsTabPanelContainer, TabHeader } from '../../util/SharedStyles';

import { useTitle } from '../../util/Common';
import { Viewer } from '../../util/Constants';
import { getFormattedDateTimeString } from '../../util/DateTimeUtility';
import { getFirstBlobStorageVersion } from '../../util/BlobStorageUtility';

import TabPanel from '../../components/TabPanel';
import DetailsPage from '../../components/DetailsPage';
import BlobFileVersionList from '../../components/lists/BlobFileVersionList';

interface SefFileDetailsProps {
    viewer: Viewer | undefined;
}

const StandardExchangeFormatFileDetails: React.FC<SefFileDetailsProps> = (props) => {
    // blobDocumentId will be passed in through router
    const { blobDocumentId } = useParams();
    const { viewer } = props;
    const [fileName, setFileName] = useState<string | undefined>(undefined);
    const [createdBy, setCreatedBy] = useState<string | undefined>(undefined);
    const [createdTime, setCreatedTime] = useState<string | undefined>(undefined);

    const [tabValue, setTabValue] = useState(0);

    // Use hook to fetch file versions
    const { fileVersions, isLoading, error } = useBlobStorageFileVersions(
        blobDocumentId,
        BlobDocumentType.Sef
    );

    // Calculate first version from file versions
    const firstVersion = useMemo(() => getFirstBlobStorageVersion(fileVersions), [fileVersions]);

    const [verticalAlign, setVerticalAlign] = useState<boolean>(false);
    // detailsPageProps
    const [detailsRecordLoaded, setDetailsRecordLoaded] = useState<boolean>(false);
    const [detailsPageTitle, setDetailsPageTitle] = useState<string | undefined>(undefined);
    const [detailsToolbarButtons, setDetailsToolbarButtons] = useState<JSX.Element[] | undefined>(undefined);
    const [detailsAV, setDetailsAV] = useState<JSX.Element | undefined>(undefined);
    const [detailsHeader, setDetailsHeader] = useState<string | undefined>(undefined);
    const [detailsSubHeader, setDetailsSubHeader] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (firstVersion) {
            if (firstVersion.fileName) {
                setFileName(firstVersion.fileName);
            }
            if (firstVersion.createdByName) {
                setCreatedBy(firstVersion.createdByName);
            }
            if (firstVersion.createdTime) {
                let formattedDate = getFormattedDateTimeString(firstVersion.createdTime, { format: DateTime.DATETIME_FULL });
                setCreatedTime(formattedDate);
            }
        }
    }, [firstVersion]);

    useTitle(fileName);

    const updateAlignment = (verticalAlign: boolean) => {
        setVerticalAlign(verticalAlign);
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const getFileDetails = () => {
        if (firstVersion) {
            return (
                <Grid container alignItems="flex-start">
                    <Grid container spacing={2} columns={{ xs: 2, sm: 2, md: 2, lg: 1, xl: 1 }}>
                        <Grid item xs={1}>
                            <Typography variant='caption' >Created On</Typography>
                            <Typography variant='body1' >{createdTime}</Typography>
                        </Grid>
                        <Grid item xs={1}>
                            <Typography variant='caption' >Created By</Typography>
                            <Typography variant='body1' >{createdBy}</Typography>
                        </Grid>
                    </Grid>
                </Grid>
            );
        }
    };

    const getTabbedContent = () => {
        if (blobDocumentId) {
            return (
                <DetailsTabContainer>
                    <DetailsTabHeaderWrapper>
                        <DetailsTabHeader value={tabValue} onChange={handleTabChange} >
                            <TabHeader label="File Versions" id='vertical-tab-0' />
                        </DetailsTabHeader>
                    </DetailsTabHeaderWrapper>
                    <DetailsTabPanelContainer>
                        <TabPanel verticalAlign={verticalAlign} value={tabValue} index={0}>
                <BlobFileVersionList
                    viewerRole={viewer?.role}
                    blobDocumentId={blobDocumentId}
                    blobDocumentType={BlobDocumentType.Sef}
                    acceptedFileTypes={{ 'text/plain': ['.sef'], 'text/xml': ['.xml'] }}
                    fileTypeLabel="SEF File"
                    allowedExtensions={['.sef', '.xml']}
                />
                        </TabPanel>
                    </DetailsTabPanelContainer>
                </DetailsTabContainer>
            );
        }
    };
    
    useEffect(() => {
        setDetailsRecordLoaded(!!firstVersion);
        if (firstVersion) {
            setDetailsPageTitle(fileName);
            setDetailsAV(<CardAvatar aria-label="sef file versions" alt="versions" >
                {<FileIcon />}
            </CardAvatar>);
            setDetailsHeader(fileName);
            setDetailsSubHeader(undefined);
            setDetailsToolbarButtons(undefined)
        }
    }, [firstVersion, fileName]);

    return (
        <DetailsPage
            recordLoaded={detailsRecordLoaded}
            pageTitle={detailsPageTitle}
            toolBarButtons={detailsToolbarButtons}
            detailsAV={detailsAV}
            detailsHeader={detailsHeader}
            detailsSubHeader={detailsSubHeader}
            detailsChips={undefined}
            detailsEditButton={undefined}
            detailsFavoriteButton={undefined}
            getDetailsListContainer={getFileDetails}
            getTabbedContainer={getTabbedContent}
            getDialogs={() => undefined}
            error={error}
            updateAlignment={updateAlignment}
        />
    );
};

export default StandardExchangeFormatFileDetails;